/**
 * OmnichannelCatalog.jsx — OMNI-03 Provider Catalog
 *
 * Displays all 12 communication providers grouped by category with:
 *   - Live status from the backend registry for implemented channels
 *   - CATALOG_ONLY with prerequisites for unimplemented channels
 *   - Category filter chips: All | Meta | Messaging | Business
 *   - Direct navigation to existing config tabs for implemented providers
 *
 * Rules:
 *   - No fake LIVE badges for MOCK or CATALOG_ONLY providers
 *   - MOCK·SIMULATED badge always shown for mock transports
 *   - Implemented ≠ Live — status is derived from actual backend config
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  MessageSquare, Mail, Globe, Activity, Users, Phone, Send, Video,
  MessageCircle, RefreshCw, AlertTriangle, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, Settings, Lock, Monitor, Zap, Radio,
  ExternalLink, ShieldAlert, Info
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Icon map (slug → lucide component + brand color) ─────────────────────────
const ICON_MAP = {
  whatsapp:      { Icon: MessageSquare, color: "#25D366" },
  instagram:     { Icon: Activity,      color: "#E1306C" },
  facebook:      { Icon: MessageCircle, color: "#1877F2" },
  facebook_leads:{ Icon: Users,         color: "#1877F2" },
  telegram:      { Icon: Send,          color: "#2CA5E0" },
  sms:           { Icon: Phone,         color: "#6B7280" },
  tiktok:        { Icon: Video,         color: "#010101" },
  line:          { Icon: MessageSquare, color: "#00B900" },
  wechat:        { Icon: Users,         color: "#07C160" },
  viber:         { Icon: Phone,         color: "#7360F2" },
  email:         { Icon: Mail,          color: "#EA4335" },
  web:           { Icon: Monitor,       color: "#7A1A2B" },
};

// ── Status display config ─────────────────────────────────────────────────────
const STATUS_DISPLAY = {
  ACTIVE: {
    dot: "#16A34A", bg: "#DCFCE7", text: "#166534",
    label: "Live", badge: "LIVE",
  },
  UAT_ACTIVE: {
    dot: "#C9A15F", bg: "#FDF2F5", text: "#7A1A2B",
    label: "UAT Enabled (Allowlist Gated)", badge: "UAT",
  },
  VALIDATE: {
    dot: "#D97706", bg: "#FEF9C3", text: "#92400E",
    label: "Validated — Pending Activation", badge: "VALIDATED",
  },
  CONFIGURE: {
    dot: "#3B82F6", bg: "#DBEAFE", text: "#1E40AF",
    label: "Setup In Progress", badge: "CONFIGURING",
  },
  UNCONFIGURED: {
    dot: "#A8A29E", bg: "#F4F0EA", text: "#57534E",
    label: "Not Configured", badge: "NOT CONFIGURED",
  },
  CATALOG_ONLY: {
    dot: "#A8A29E", bg: "#F1F5F9", text: "#475569",
    label: "Prerequisites Required", badge: "CATALOG ONLY",
  },
  PAUSED: {
    dot: "#8B5CF6", bg: "#EDE9FE", text: "#7C3AED",
    label: "Paused", badge: "PAUSED",
  },
  DISCONNECTED: {
    dot: "#EF4444", bg: "#FEE2E2", text: "#991B1B",
    label: "Disconnected", badge: "DISCONNECTED",
  },
};

const CATEGORY_META = {
  ALL:       { label: "All",       color: "#1C1917" },
  META:      { label: "Meta",      color: "#1877F2" },
  MESSAGING: { label: "Messaging", color: "#7A1A2B" },
  BUSINESS:  { label: "Business",  color: "#166534" },
};

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusBadge({ status, isMock }) {
  const cfg = STATUS_DISPLAY[status] || STATUS_DISPLAY.UNCONFIGURED;
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cfg.dot }} />
      <span className="text-[10px] font-semibold tracking-wide" style={{ color: cfg.text }}>
        {isMock ? "MOCK · SIMULATED" : cfg.badge}
      </span>
    </div>
  );
}

function ProviderCard({ provider, onNavigate }) {
  const [prereqOpen, setPrereqOpen] = useState(false);
  const liveStatus = provider.live_status || {};
  const statusKey = liveStatus.status || "CATALOG_ONLY";
  const statusCfg = STATUS_DISPLAY[statusKey] || STATUS_DISPLAY.CATALOG_ONLY;
  const isMock = liveStatus.is_mock;
  const isLive = liveStatus.is_live;
  const isCatalogOnly = !provider.implemented;
  const { Icon, color } = ICON_MAP[provider.icon_slug] || { Icon: Zap, color: "#6B7280" };

  const hasConfigTab = !!provider.config_tab;
  const hasConfigRoute = !!provider.config_route;

  return (
    <div
      data-testid={`provider-card-${provider.provider_id}`}
      className="flex flex-col rounded-xl transition-all"
      style={{
        background: "#FFFFFF",
        border: `1px solid ${isCatalogOnly ? "#E2E8F0" : "#E5E0D8"}`,
        opacity: isCatalogOnly ? 0.88 : 1,
      }}
    >
      {/* Card header */}
      <div className="flex items-start justify-between p-4 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}18`, border: `1px solid ${color}30` }}
          >
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: "#1C1917" }}>
              {provider.display_name}
            </p>
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
              style={{ background: statusCfg.bg, color: statusCfg.text }}
            >
              {provider.category}
            </span>
          </div>
        </div>

        {/* Live/UAT/Mock top-right badge */}
        <div className="flex-shrink-0 ml-2">
          {isLive && statusKey === "ACTIVE" && (
            <span
              data-testid={`live-badge-${provider.provider_id}`}
              className="text-[9px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" }}
            >
              LIVE
            </span>
          )}
          {statusKey === "UAT_ACTIVE" && (
            <span
              data-testid={`uat-badge-${provider.provider_id}`}
              className="text-[9px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#FDF2F5", color: "#7A1A2B", border: "1px solid #C9A15F" }}
            >
              UAT
            </span>
          )}
          {isMock && (
            <span
              data-testid={`mock-badge-${provider.provider_id}`}
              className="text-[9px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#FEF9C3", color: "#92400E", border: "1px solid #FDE047" }}
            >
              MOCK
            </span>
          )}
          {isCatalogOnly && (
            <span
              data-testid={`catalog-badge-${provider.provider_id}`}
              className="text-[9px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #CBD5E1" }}
            >
              CATALOG
            </span>
          )}
        </div>
      </div>

      {/* Purpose */}
      <p className="text-xs px-4 pb-3 leading-relaxed" style={{ color: "#78716C" }}>
        {provider.purpose}
      </p>

      {/* Status row */}
      <div className="flex items-center gap-2 px-4 pb-3">
        <StatusBadge status={statusKey} isMock={isMock} />
        {liveStatus.connection_label && (
          <span className="text-[10px]" style={{ color: "#A8A29E" }}>
            · {liveStatus.connection_label}
          </span>
        )}
      </div>

      {/* UAT notice */}
      {statusKey === "UAT_ACTIVE" && liveStatus.uat_notice && (
        <div
          className="mx-4 mb-3 px-2.5 py-1.5 rounded-lg text-[10px]"
          style={{ background: "#FDF2F5", color: "#7A1A2B", border: "1px solid #f5c6d0" }}
        >
          {liveStatus.uat_notice}
        </div>
      )}

      {/* Prerequisites accordion — shown for all providers that have prerequisites */}
      {provider.prerequisites && provider.prerequisites.length > 0 && (
        <div className="px-4 pb-3">
          <button
            data-testid={`prereq-toggle-${provider.provider_id}`}
            onClick={() => setPrereqOpen(v => !v)}
            className="flex items-center gap-1 text-[10px] font-medium transition-colors"
            style={{ color: "#78716C" }}
          >
            {prereqOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {isCatalogOnly
              ? `${provider.prerequisites.length} prerequisites to implement`
              : `${provider.prerequisites.length} activation prerequisites`}
          </button>
          {prereqOpen && (
            <ul className="mt-2 space-y-1">
              {provider.prerequisites.map((req, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[10px]" style={{ color: "#57534E" }}>
                  <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#C9A15F" }} />
                  {req}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Live activation blockers — for implemented but not live providers */}
      {provider.implemented && !isLive && provider.live_activation_blockers?.length > 0 && (
        <div
          className="mx-4 mb-3 px-2.5 py-1.5 rounded-lg"
          style={{ background: "#FEF9C3", border: "1px solid #FDE047" }}
        >
          <p className="text-[9px] font-semibold mb-1" style={{ color: "#92400E" }}>
            Not Yet Live:
          </p>
          {provider.live_activation_blockers.map((b, i) => (
            <p key={i} className="text-[9px]" style={{ color: "#92400E" }}>· {b}</p>
          ))}
        </div>
      )}

      {/* Action footer */}
      <div className="mt-auto px-4 pb-4 pt-1">
        {hasConfigTab && (
          <button
            data-testid={`configure-btn-${provider.provider_id}`}
            onClick={() => onNavigate(provider.config_tab)}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg w-full justify-center transition-all"
            style={{
              background: isLive ? "#7A1A2B" : "#F4F0EA",
              color: isLive ? "#FAF6F0" : "#57534E",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = isLive ? "#5A1020" : "#E8E2DA";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = isLive ? "#7A1A2B" : "#F4F0EA";
            }}
          >
            <Settings className="w-3.5 h-3.5" />
            {statusKey === "UNCONFIGURED" ? "Set Up Channel" : "Manage Configuration"}
          </button>
        )}
        {!hasConfigTab && hasConfigRoute && (
          <a
            href={provider.config_route}
            data-testid={`config-link-${provider.provider_id}`}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg w-full justify-center transition-all"
            style={{ background: "#DCFCE7", color: "#166534", textDecoration: "none" }}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open Setup Studio
          </a>
        )}
        {isCatalogOnly && (
          <div
            data-testid={`catalog-only-notice-${provider.provider_id}`}
            className="flex items-center gap-1.5 text-[10px] px-3 py-1.5 rounded-lg justify-center"
            style={{ background: "#F1F5F9", color: "#64748B", border: "1px solid #E2E8F0" }}
          >
            <Lock className="w-3 h-3" />
            Catalog display only — prerequisites required
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main OmnichannelCatalog component ─────────────────────────────────────────

export default function OmnichannelCatalog({ onNavigateToChannel }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("ALL");

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/comms/catalog`, { withCredentials: true });
      setCatalog(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load provider catalog.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const providers = catalog?.providers || [];
  const summary   = catalog?.summary || {};

  // Filter by active category
  const filtered = activeCategory === "ALL"
    ? providers
    : providers.filter(p => p.category === activeCategory);

  // Category counts
  const counts = {
    ALL:       providers.length,
    META:      providers.filter(p => p.category === "META").length,
    MESSAGING: providers.filter(p => p.category === "MESSAGING").length,
    BUSINESS:  providers.filter(p => p.category === "BUSINESS").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="catalog-loading">
        <div
          className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#C9A15F", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl" style={{ background: "#FEE2E2" }}
        data-testid="catalog-error">
        <AlertTriangle className="w-4 h-4" style={{ color: "#991B1B" }} />
        <span className="text-sm" style={{ color: "#991B1B" }}>{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="omnichannel-catalog">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-bold" style={{ color: "#1C1917" }}>
            Provider Catalog
          </h3>
          <p className="text-xs mt-0.5" style={{ color: "#78716C" }}>
            All available communication providers — {summary.implemented} implemented,{" "}
            {summary.catalog_only} requiring prerequisites.
          </p>
        </div>
        <button
          data-testid="catalog-refresh-btn"
          onClick={loadCatalog}
          className="p-1.5 rounded-lg transition-colors"
          style={{ background: "#F4F0EA", color: "#78716C" }}
          title="Refresh catalog"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="catalog-summary">
        {[
          { label: "Total Providers", value: summary.total || 0,       color: "#57534E" },
          { label: "Implemented",     value: summary.implemented || 0, color: "#7A1A2B" },
          { label: "Live Channels",   value: summary.live_count || 0,  color: "#166534" },
          { label: "Mock / Simulated",value: summary.mock_count || 0,  color: "#92400E" },
        ].map(({ label, value, color }) => (
          <div
            key={label}
            className="p-3 rounded-xl text-center"
            style={{ background: "#FFFFFF", border: "1px solid #E5E0D8" }}
          >
            <p className="text-xl font-bold" style={{ color }}>{value}</p>
            <p className="text-[10px] mt-0.5" style={{ color: "#78716C" }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Category filter chips */}
      <div
        className="flex gap-1.5 p-1 rounded-xl overflow-x-auto"
        style={{ background: "#F4F0EA" }}
        data-testid="category-filter"
      >
        {Object.entries(CATEGORY_META).map(([cat, meta]) => {
          const isActive = activeCategory === cat;
          return (
            <button
              key={cat}
              data-testid={`category-filter-${cat.toLowerCase()}`}
              onClick={() => setActiveCategory(cat)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0"
              style={{
                background: isActive ? "#FFFFFF" : "transparent",
                color: isActive ? meta.color : "#78716C",
                boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              }}
            >
              {meta.label}
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                style={{
                  background: isActive ? `${meta.color}18` : "#E5E0D8",
                  color: isActive ? meta.color : "#A8A29E",
                }}
              >
                {counts[cat]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Provider grid */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        data-testid="provider-grid"
      >
        {filtered.map(provider => (
          <ProviderCard
            key={provider.provider_id}
            provider={provider}
            onNavigate={onNavigateToChannel}
          />
        ))}
      </div>

      {/* OMNI-03 compliance notice */}
      <div
        className="p-3 rounded-xl text-xs flex items-start gap-2"
        style={{ background: "#F4F0EA", border: "1px solid #E5E0D8" }}
        data-testid="omni-compliance-notice"
      >
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#78716C" }} />
        <div style={{ color: "#78716C" }}>
          <strong style={{ color: "#1C1917" }}>OMNI-03 Compliance: </strong>
          MOCK and CATALOG ONLY providers do not deliver to real recipients.
          Status indicators reflect verified backend configuration — not assumed capability.
          New provider adapters require separate engineering approval and verified external prerequisites.
        </div>
      </div>
    </div>
  );
}
